<template>
  <div
    class="project-card clickable"
    @click="$emit('click', project.project_id)"
  >
    <div class="project-info">
      <div class="project-id">Проект #{{ project.project_id }}</div>
      <div class="tw-rounded-full tw-p-2 tw-flex tw-justify-center tw-items-center tw-border tw-border-[#727272] ">
        <SvgIcon name="arrowUpRight" color="#727272" style="width: 13px; height: 13px;" />
      </div>
    </div>

    <div class="tw-flex tw-justify-between tw-gap-w tw-items-center tw-mb-4">
      <div class="dates">
        <span class="date">{{ formatDate(project.start_date) }}</span>
        <span class="separator">—</span>
        <span class="date">{{ formatDate(project.finish_date) }}</span>
      </div>
      
      <div v-if="project.block_name" class="blocks">
        {{ project.block_name }}
        <q-tooltip>Блоки</q-tooltip>
      </div>
    </div>
    <div class="timeline">
      <!-- <div class="timeline-header">
        <div class="timeline-label">
          <q-icon name="schedule" size="16px" color="grey-7" />
          <span class="timeline-title">Сроки проекта</span>
        </div>
      </div> -->
      <div class="timeline-stats">
        <span class="timeline-stat">Прошло: {{ elapsedDays }} дн.</span>
        <div class="timeline-circle">
          <svg
            :width="circleSize"
            :height="circleSize"
            :viewBox="`0 0 ${circleSize} ${circleSize}`"
            role="img"
            :aria-label="`Сроки проекта: прошло ${Math.round(elapsedPercent)}% (${elapsedDays} из ${totalDays} дней)`"
          >
            <circle
              class="circle-track"
              :cx="center"
              :cy="center"
              :r="radius"
              :stroke-width="strokeWidth"
              fill="none"
            />
            <circle
              v-if="isOverdue"
              class="circle-overdue"
              :cx="center"
              :cy="center"
              :r="radius"
              stroke-width="2"
              fill="none"
            />
            <circle
              class="circle-progress"
              :class="timelineColorClass"
              :cx="center"
              :cy="center"
              :r="radius"
              :stroke-width="strokeWidth"
              fill="none"
              stroke-linecap="round"
              :stroke-dasharray="circumference"
              :stroke-dashoffset="dashOffset"
              :transform="`rotate(-90 ${center} ${center})`"
            />
          </svg>
          <div class="circle-label" :class="timelineColorClass">
            <template v-if="!isCircleLoading">{{ Math.round(elapsedPercent) }}%</template>
            <q-spinner v-else size="16px" color="grey-7" />
          </div>
          <q-tooltip>
            Начало: {{ project.start_date || '—' }}<br />
            Завершение: {{ project.finish_date || '—' }}
          </q-tooltip>
        </div>
        <span class="timeline-stat" :class="remainingColorClass" style="color: #e15a1c; font-weight: 500;">Осталось: {{ remainingDays }} дн.</span>
        <div class="tw-flex tw-justify-center tw-items-center tw-w-full" style="margin-top: -6px;">
          <span class="timeline-stat total-days">{{ totalDays }} {{ totalDaysWord }}</span>
        </div>
      </div>
    </div>

    <div class="progress tw-mt-4">
      <div class="progress-text">
        {{ Math.round(project.progress_percent) }}%
      </div>
      <div class="progress-bar">
        <div
          class="progress-fill"
          :style="{ width: `${Math.min(project.progress_percent, 100)}%` }"
          :class="{ 'completed': project.progress_percent >= 100 }"
        ></div>
      </div>
    </div>
    <q-tooltip anchor="top middle">Перейти на страницу проекта</q-tooltip>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import SvgIcon from '@/components/common/SvgIcon';
const props = defineProps({
  project: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['click'])

// Форматирование даты из DD.MM.YYYY в читаемый формат
const formatDate = (dateString) => {
  if (!dateString) return '—'

  const [day, month, year] = dateString.split('.')
  const date = new Date(year, month - 1, day)

  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

// Подсчёт прошедших и оставшихся дней
const parseDMY = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null
  const parts = dateString.split('.')
  if (parts.length !== 3) return null
  const dd = Number(parts[0])
  const mm = Number(parts[1])
  const yyyy = Number(parts[2])
  const d = new Date(yyyy, (mm || 1) - 1, dd || 1)
  return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

const diffInDays = (a, b) => {
  const MS = 24 * 60 * 60 * 1000
  return Math.floor((b.getTime() - a.getTime()) / MS)
}

const todayStart = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())
const start = parseDMY(props.project?.start_date)
const finish = parseDMY(props.project?.finish_date)

const totalDays = (() => {
  if (!start || !finish) return 0
  const d = diffInDays(start, finish)
  return d < 0 ? 0 : d
})()

const elapsedDays = (() => {
  if (!start) return 0
  const d = diffInDays(start, todayStart)
  if (d < 0) return 0
  if (totalDays === 0) return 0
  return Math.min(d, totalDays)
})()

const remainingDays = totalDays > 0 ? Math.max(totalDays - elapsedDays, 0) : 0
const elapsedPercent = totalDays > 0 ? Math.min(Math.max((elapsedDays / totalDays) * 100, 0), 100) : 0
const remainingPercent = totalDays > 0 ? (remainingDays / totalDays) * 100 : 100
const progressPercent = Number(props.project?.progress_percent ?? 0)
const isCompletedAndTime100 = computed(() => progressPercent >= 100 && elapsedPercent >= 100)

// Для просрочки (>100% времени при прогрессе < 100%)
const elapsedDaysRaw = (() => {
  if (!start) return 0
  const d = diffInDays(start, todayStart)
  return d < 0 ? 0 : d
})()
const isOverdue = computed(() => totalDays > 0 && elapsedDaysRaw > totalDays && progressPercent < 100)

const timelineColorClass = computed(() => {
  if (isCompletedAndTime100.value) return 'ok'
  if (remainingPercent > 50) return 'ok'
  if (remainingPercent > 20) return 'warn'
  return 'danger'
})

// Circle progress geometry
const circleSize = 64
const strokeWidth = 6
const center = circleSize / 2
const radius = (circleSize - strokeWidth) / 2
const circumference = 2 * Math.PI * radius
const dashOffset = computed(() => circumference * (1 - (elapsedPercent / 100)))

// Мини-лоадер на круге, если не хватает данных по датам
const isCircleLoading = computed(() => !start || !finish || totalDays === 0)

// Склонение "день/дня/дней" для totalDays
const getDaysWord = (n) => {
  const abs = Math.abs(n) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20) return 'дней'
  if (last === 1) return 'день'
  if (last >= 2 && last <= 4) return 'дня'
  return 'дней'
}
const totalDaysWord = computed(() => getDaysWord(totalDays))
</script>

<style scoped lang="scss">
.project-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 4px #eee;
}

.project-card.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: var(--q-primary);
}

.project-card.clickable:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.project-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 96, 254, 0.04), transparent);
  transition: left 0.5s ease;
}

.project-card.clickable:hover::before {
  left: 100%;
}

.project-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.project-id {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.blocks {
  font-size: 13px;
  color: #666;
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 6px;
}

.dates {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
}

.separator {
  color: #999;
}

.progress {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-text {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  min-width: 40px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #ff8c00;
  border-radius: 3px;
  transition: width 0.3s ease;

  &.completed {
    background: #28a745;
  }
}

.timeline {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.timeline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  color: #6b7280; /* gray-500 */
  font-size: 12px;
  line-height: 12px;
}

.timeline-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.timeline-title {
  font-weight: 500;
}

.timeline-percent {
  font-weight: 600;
  color: #374151; /* gray-700 */
}

.timeline-circle {
  position: relative;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2px 0;
}

.circle-track {
  stroke: #eceff3;
}
.circle-overdue { stroke: #ef4444; } /* red-500 thin ring */
.circle-progress.ok { stroke: #10b981; } /* green-500 */
.circle-progress.warn { stroke: #f59e0b; } /* amber-500 */
.circle-progress.danger { stroke: #ef4444; } /* red-500 */

.circle-label {
  position: absolute;
  font-size: 14px;
  font-weight: 600;
  color: #374151; /* gray-700 */
  display: flex;
  align-items: center;
  justify-content: center;
}
.circle-label.ok { color: #10b981; }
.circle-label.warn { color: #f59e0b; }
.circle-label.danger { color: #ef4444; }

.timeline-stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.timeline-stat {
  font-size: 13px;
  line-height: 12px;
  color: #6b7280; /* gray-500 */
}

.timeline-stat.total-days {
  color: #9ca3af; /* gray-400 */
}

.chip {
  font-size: 12px;
  line-height: 12px;
  padding: 6px 8px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #6b7280; /* gray-500 */

  &--elapsed {
    border-color: #93c5fd;
    background: #eff6ff;
    color: #6b7280; /* gray-500 */
  }

  &--left {
    border-color: #fcd34d;
    background: #fffbeb;
    color: #6b7280; /* gray-500 */
  }
}

@media (max-width: 768px) {
  .project-card {
    padding: 12px;
  }

  .project-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .dates {
    flex-wrap: wrap;
  }
}
</style>
